<template>
  <div class="params-container">
    <div class="aspect-ratio-section">
      <div class="section-title">{{ t('conversation.imageParams.whatAspectRatio') }}</div>
      <div class="aspect-ratio-buttons">
        <BbBtn
          :is-disabled="isTyping"
          v-for="(ratio, index) in aspectRatios"
          :key="`${ratio.value}-${index}`"
          :class="['aspect-ratio-button', ratio.class, selectedAspectRatioIndex === index ? 'selected' : '']"
          size="small"
          type="icon"
          no-background
          @click="selectAspectRatio(index, ratio.value)"
        >
          <div class="aspect-ratio-form" :class="ratio.iconClass"></div>
        </BbBtn>
      </div>
    </div>

    <BbSelectList
      v-model="selectedStyle"
      :placeholder="t('conversation.imageParams.wouldYouLikeToUsePredefinedStyle')"
      width="50%"
    >
      <BbSelectListItem v-for="style in brandStylesWithNone" :key="style.id" :label="style.name" :value="style.id">
        <BaseImage v-if="style.url" class="style-preview" :src="style.url" alt="style preview" />
        <div v-else class="style-preview style-preview-placeholder"></div>
        <div class="style-details">
          <div class="style-name">{{ style.name }}</div>
          <div class="style-description">{{ style.description }}</div>
        </div>
      </BbSelectListItem>
    </BbSelectList>

    <div class="actions-section">
      <BbBtn :is-disabled="isTyping" class="create-button" color="#6222e3" @click="handleCreate"
        >{{ t('common.create') }}
      </BbBtn>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { IMAGE_ASPECT_RATIOS } from '~/constants/conversationModels';
import useConversations from '~/composables/useConversations';
import useImageGeneration from '~/composables/useImageGeneration';
import BaseImage from '~/components/common/BaseImage.vue';

export default {
  name: 'GeneratedImageParams',
  components: { BaseImage },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    messageId: {
      type: String,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const { brandStyles, triggerImageGeneration, imageGenerationParams } = useImageGeneration();
    const { isTyping, onPopMessage } = useConversations();

    // Add null option at the beginning of brand styles list
    const brandStylesWithNone = computed(() => {
      const noneOption = {
        id: 'none',
        value: null,
        name: t('conversation.imageParams.dontUsePredefinedStyle'),
        description: t('conversation.imageParams.promptControlsLookAndFeel'),
        url: null,
      };
      return [noneOption, ...(brandStyles.value || [])];
    });

    const aspectRatios = IMAGE_ASPECT_RATIOS;
    const selectedAspectRatioIndex = ref(0);
    const selectedAspectRatio = ref(IMAGE_ASPECT_RATIOS[0].value);
    const selectedStyle = ref(null);

    // Initialize from store imageGenerationParams if it exists
    const getInitialAspectRatio = () => {
      if (imageGenerationParams.value?.width && imageGenerationParams.value?.height) {
        const { width, height } = imageGenerationParams.value;
        const aspectRatioString = `${width}:${height}`;
        const index = IMAGE_ASPECT_RATIOS.findIndex((ratio) => ratio.value === aspectRatioString);
        if (index !== -1) {
          return { index, value: aspectRatioString };
        }
      }
      return { index: 0, value: IMAGE_ASPECT_RATIOS[0].value };
    };

    const selectAspectRatio = (index, value) => {
      selectedAspectRatioIndex.value = index;
      selectedAspectRatio.value = value;
    };

    const handleCreate = () => {
      triggerImageGeneration(props.messageId, selectedAspectRatio.value, selectedStyle.value);
      onPopMessage(props.messageId);
    };

    const getInitialValues = async () => {
      const initialAspectRatio = getInitialAspectRatio();
      selectedAspectRatioIndex.value = initialAspectRatio.index || 0;
      selectedAspectRatio.value = initialAspectRatio.value || IMAGE_ASPECT_RATIOS[0].value;
      selectedStyle.value = imageGenerationParams.value?.brandStyleId || null;
    };

    onMounted(async () => {
      await getInitialValues();
    });

    return {
      t,
      aspectRatios,
      isTyping,
      selectedAspectRatioIndex,
      selectedAspectRatio,
      selectedStyle,
      selectAspectRatio,
      handleCreate,
      brandStylesWithNone,
    };
  },
};
</script>

<style scoped lang="scss">
.params-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;
  margin-top: $bb-spacing-4;

  .bb-select-list {
    :deep(.el-select-dropdown__item) {
      min-width: 0;
      overflow: hidden;
      width: 100%;
      background-color: $bb-neutral-0;
      border: none !important;

      &.is-selected {
        background-color: transparent !important;
        color: $bb-primary-3 !important;
      }
    }

    :deep(.el-select-dropdown) {
      min-width: 100%;
    }

    .bb-select-list-item {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      gap: $bb-spacing-2;
      min-width: 0;
      width: 100%;
      padding: $bb-spacing-2 !important;

      .style-preview {
        width: 70px !important;
        height: 50px !important;
        border-radius: $bb-spacing-1;
        flex-shrink: 0;
      }

      .style-preview-placeholder {
        background-color: $bb-neutral-4;
      }

      .style-details {
        display: flex;
        flex-direction: column;
        min-width: 0;
        flex: 1;
        overflow: hidden;
      }

      .style-name {
        @include bb-medium-text-styles('md');
        font-weight: 800;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
      }

      .style-description {
        @include bb-medium-text-styles('sm');
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-word;
        white-space: normal;
        max-width: 100%;
      }
    }
  }

  .bb-select-list-item.large.el-select-dropdown__item {
    height: fit-content !important;
  }
}

.aspect-ratio-section {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-3;
}

.section-title {
  word-break: break-word;
  font-size: 15px;
  line-height: $bb-spacing-5;
  color: $bb-neutral-1;
}

.aspect-ratio-buttons {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-4;
  align-items: center;
  width: 100%;
}

.aspect-ratio-button {
  background: transparent !important;
  border: 1px solid $bb-neutral-4 !important;
  border-radius: 0 !important;
  min-width: auto !important;
  width: 60px !important;
  height: 60px !important;

  &.selected {
    border-color: $bb-primary-3 !important;
    border-width: 2px !important;
  }
}

.square {
  background-color: $bb-neutral-4 !important;
}

.aspect-ratio-form {
  background: $bb-neutral-4;

  &.vertical-wide {
    width: 40px;
    height: 56px;
  }

  &.vertical {
    width: 25px;
    height: 58px;
  }

  &.horizontal {
    width: 60px;
    height: 20px;
  }

  &.horizontal-wide {
    width: 58px;
    height: 40px;
  }
}

.actions-section {
  display: flex;
  justify-content: flex-end;
  margin-top: $bb-spacing-2;
}
</style>
